import { Box, Button, IconButton, Switch, TextField, Tooltip } from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Typography from '@material-ui/core/Typography';
import LensIcon from '@material-ui/icons/Lens';
import React, { useContext, useState } from 'react';
import settings from '../services/settings';
import themes, { ThemeColorPresets } from '../theme';
import { ThemeContext } from './ThemeContext';

interface IThemeColorItem {
  colorHex: string;
  name: string;
  onClick: (colorHex: string) => void;
}

const ThemeColorItem = (props: IThemeColorItem): JSX.Element => {
  const { colorHex, name, onClick } = props;

  return (
    <div>
      <Tooltip title={name} arrow>
        <IconButton
          onClick={() => {
            onClick(colorHex);
          }}
          edge="start"
        >
          <LensIcon style={{ color: colorHex, fontSize: '48px' }} />
        </IconButton>
      </Tooltip>
    </div>
  );
};

const SettingDropdownMenu = (): JSX.Element => {
  const [legoURI, setLegoURI] = useState<string>(settings.getLegoURI() as string);
  const [crewURI, setCrewURI] = useState<string>(settings.getCrewURI() as string);
  const [apiURI, setApiURI] = useState<string>(settings.getApiURI() as string);

  const [isShowSavedPromptLegoURI, setIsShowSavedPromptLegoURI] = useState<boolean>(false);
  const [isShowSavedPromptCrewURI, setIsShowSavedPromptCrewURI] = useState<boolean>(false);
  const [isShowSavedPromptApiURI, setIsShowSavedPromptApiURI] = useState<boolean>(false);

  const setTheme = useContext(ThemeContext);

  const applyApiURI = () => {
    settings.setApiURI(apiURI);
    console.log('API URI has been changed:', apiURI);
    setIsShowSavedPromptApiURI(true);
    setTimeout(() => {
      setIsShowSavedPromptApiURI(false);
    }, 3000);
  };

  const applyCrewURI = () => {
    settings.setCrewURI(crewURI);
    console.log('Crew URI has been changed:', crewURI);
    setIsShowSavedPromptCrewURI(true);

    // FIXME: it must be cleared when the component has unmounted. (cause memory leak)
    setTimeout(() => {
      setIsShowSavedPromptCrewURI(false);
    }, 3000);
  };

  const applyLegoURI = () => {
    settings.setLegoURI(legoURI);
    console.log('Lego URI has been changed:', legoURI);
    setIsShowSavedPromptLegoURI(true);

    // FIXME: it must be cleared when the component has unmounted. (cause memory leak)
    setTimeout(() => {
      setIsShowSavedPromptLegoURI(false);
    }, 3000);
  };

  const handleClickThemeColorItem = (colorHex: string) => {
    settings.setThemeColor(colorHex);
    setTheme(themes.getTheme(colorHex));
  };

  return (
    <div style={{ width: '280px' }}>
      {process.env.ENV !== 'production' && (
        <React.Fragment>
          <List component="nav">
            <ListItem>
              <Typography variant="caption" color={'secondary'}>
                You are running on a dev mode.
              </Typography>
            </ListItem>

            <ListItem>
              <Typography variant="subtitle2">Set API URL</Typography>
            </ListItem>
            <ListItem>
              <TextField
                style={{ width: '100%' }}
                value={apiURI}
                onChange={e => setApiURI(e.target.value)}
                onKeyPress={e => {
                  if (e.key === 'Enter') {
                    applyApiURI();
                  }
                }}
              />
            </ListItem>
            <ListItem>
              <Box display="flex" flexDirection="row" width="100%">
                <Button
                  variant={isShowSavedPromptApiURI ? 'contained' : 'outlined'}
                  size="small"
                  color="primary"
                  style={{ flex: 1 }}
                  onClick={() => {
                    applyApiURI();
                  }}
                >
                  {isShowSavedPromptApiURI ? 'Saved!' : 'Apply'}
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  style={{ flex: 1, marginLeft: '8px' }}
                  onClick={() => {
                    settings.setApiURIDefault();
                    setApiURI(settings.getApiURI() as string);
                    console.log('API URI has been changed:', settings.getApiURI());
                  }}
                >
                  Default
                </Button>
              </Box>
            </ListItem>

            <ListItem>
              <Typography variant="subtitle2">Set Crew URL</Typography>
            </ListItem>
            <ListItem>
              <TextField
                style={{ width: '100%' }}
                value={crewURI}
                onChange={e => setCrewURI(e.target.value)}
                onKeyPress={e => {
                  if (e.key === 'Enter') {
                    applyCrewURI();
                  }
                }}
              />
            </ListItem>
            <ListItem>
              <Box display="flex" flexDirection="row" width="100%">
                <Button
                  variant={isShowSavedPromptCrewURI ? 'contained' : 'outlined'}
                  size="small"
                  color="primary"
                  style={{ flex: 1 }}
                  onClick={() => {
                    applyCrewURI();
                  }}
                >
                  {isShowSavedPromptCrewURI ? 'Saved!' : 'Apply'}
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  style={{ flex: 1, marginLeft: '8px' }}
                  onClick={() => {
                    settings.setCrewURIDefault();
                    setCrewURI(settings.getCrewURI() as string);
                    console.log('Crew URI has been changed:', settings.getCrewURI());
                  }}
                >
                  Default
                </Button>
              </Box>
            </ListItem>

            <ListItem>
              <Typography variant="subtitle2">Set Lego URL</Typography>
            </ListItem>
            <ListItem>
              <TextField
                style={{ width: '100%' }}
                value={legoURI}
                onChange={e => setLegoURI(e.target.value)}
                onKeyPress={e => {
                  if (e.key === 'Enter') {
                    applyLegoURI();
                  }
                }}
              />
            </ListItem>
            <ListItem>
              <Box display="flex" flexDirection="row" width="100%">
                <Button
                  variant={isShowSavedPromptLegoURI ? 'contained' : 'outlined'}
                  size="small"
                  color="primary"
                  style={{ flex: 1 }}
                  onClick={() => {
                    applyLegoURI();
                  }}
                >
                  {isShowSavedPromptLegoURI ? 'Saved!' : 'Apply'}
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  style={{ flex: 1, marginLeft: '8px' }}
                  onClick={() => {
                    settings.setLegoURIDefault();
                    setLegoURI(settings.getLegoURI() as string);
                    console.log('Lego URI has been changed:', settings.getLegoURI());
                  }}
                >
                  Default
                </Button>
              </Box>
            </ListItem>
          </List>
          <Divider />
        </React.Fragment>
      )}

      <List component="nav">
        <ListItem>
          <Typography variant="subtitle2">Themes</Typography>
        </ListItem>
        <ListItem
          style={{
            flex: 1,
            paddingTop: 0,
          }}
        >
          <Box
            display="flex"
            flexDirection="row"
            style={{
              flex: 1,
              justifyContent: 'flex-end',
            }}
          >
            {ThemeColorPresets.map(tc => (
              <ThemeColorItem colorHex={tc.hex} name={tc.name} onClick={handleClickThemeColorItem} key={tc.hex} />
            ))}
          </Box>
        </ListItem>
      </List>
      <Divider />
      <List component="nav" style={{ padding: 0 }}>
        <ListItem>
          <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" width="100%">
            <Typography variant="subtitle2">Dark Mode</Typography>
            <Switch
              checked={settings.getDarkMode()}
              color="primary"
              onChange={() => {
                settings.setDarkMode(!settings.getDarkMode());
                setTheme(themes.getTheme());
              }}
            />
          </Box>
        </ListItem>
      </List>
      <Divider />

      <List component="nav">
        <ListItem>
          <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" width="100%">
            <Typography variant="subtitle2">Version</Typography>
            <Typography variant="caption">{settings.getVersion()}</Typography>
          </Box>
        </ListItem>
      </List>
    </div>
  );
};

export default SettingDropdownMenu;
